import { FlowAction } from '~/graphql/types';
import type { ClientFlowAction } from '~/graphql/types.client';
import getDeepestLeaf from '../../../../utils/getDeepestLeaf';
import { PathType } from '../../../../components/nodeTypes/components/IfElseCard/types';
import { reportAndThrow } from '~/util/reportAndThrow';

/**
 * Determines which action in the cloned branch will become the new parent of
 * the existing `effectivePasteTarget` action, and wires up an IfElse leaf's
 * chosen path when the user explicitly picked one.
 *
 * Returns the updated cloned branch (new array when an IfElse is patched) and
 * the id of the connection action, or `null` when the branch has no leaves.
 */
const resolveConnectionActionId = ({
  clonedBranch,
  chosenLeafOriginalId,
  chosenIfElsePath,
  effectivePasteTarget,
}: {
  clonedBranch: Array<ClientFlowAction>;
  chosenLeafOriginalId: string | null;
  chosenIfElsePath: PathType | null;
  effectivePasteTarget: string;
}): {
  connectionActionId: string | null;
  updatedBranch: Array<ClientFlowAction>;
} => {
  let updatedBranch = [...clonedBranch];

  if (chosenLeafOriginalId) {
    const chosenIdx = updatedBranch.findIndex(
      a => a._copiedFromId === chosenLeafOriginalId,
    );

    if (chosenIdx !== -1) {
      const chosen = updatedBranch[chosenIdx];

      if (chosen.actionType === FlowAction.IfElse) {
        const pathKey =
          chosenIfElsePath === PathType.False
            ? 'falseChildId'
            : chosenIfElsePath === PathType.True
              ? 'trueChildId'
              : null;

        if (pathKey === null) {
          return reportAndThrow(
            'Path key is null when choosing path from IfElse action',
          );
        }

        updatedBranch = [...updatedBranch];
        updatedBranch[chosenIdx] = {
          ...chosen,
          [pathKey]: effectivePasteTarget,
        } as ClientFlowAction;
      }

      return { connectionActionId: updatedBranch[chosenIdx].id, updatedBranch };
    }
  }

  const deepestLeaf = getDeepestLeaf(updatedBranch);
  return { connectionActionId: deepestLeaf?.id ?? null, updatedBranch };
};

export default resolveConnectionActionId;
